/**
 * Platform Admin Page
 *
 * Super Admin dashboard for managing platform-wide settings including:
 * - Blocked email domains
 * - Client organizations and their BoldSign status
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Button,
  Snackbar,
} from '@mui/material';
import {
  Block,
  Business,
  Search,
  ExpandMore,
  ExpandLess,
  BugReport,
  Category,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { BlockedDomainsManager, ClientBoldSignManager, ServiceTypesManager, SubscriptionOverrideManager } from '../../components/admin';
import { api } from '../../services/api';
import { captureException } from '../../config/sentry';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`platform-admin-tabpanel-${index}`}
      aria-labelledby={`platform-admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

type SubscriptionTier = 'trial' | 'launch' | 'rise' | 'summit' | 'expired';
type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'cancelled' | 'expired';

interface Organization {
  _id: string;
  name: string;
  slug: string;
  email?: string;
  subscription?: {
    tier?: SubscriptionTier;
    status?: SubscriptionStatus;
    currentPeriodEnd?: string;
    trialEndsAt?: string;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    adminOverride?: {
      overriddenAt: string;
      overriddenBy: string;
      notes: string;
    };
  };
  boldsign?: {
    status: string;
    senderIdentityEmail?: string;
  };
  createdAt: string;
}

export default function PlatformAdminPage() {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Check if user is super admin
  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch organizations function
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/organizations');
      setOrganizations(response.data.data.organizations);
    } catch (err) {
      setError('Failed to load organizations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch organizations on mount
  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access denied. This page is only available to platform administrators.
        </Alert>
      </Box>
    );
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleToggleExpand = (orgId: string) => {
    setExpandedOrg(expandedOrg === orgId ? null : orgId);
  };

  // Filter organizations
  const filteredOrgs = organizations.filter(
    (org) =>
      org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getBoldsignStatusChip = (status?: string) => {
    if (!status || status === 'not_configured') {
      return <Chip label="Not Configured" size="small" color="default" />;
    }
    if (status === 'pending_approval') {
      return <Chip label="Pending" size="small" color="warning" />;
    }
    if (status === 'approved') {
      return <Chip label="Approved" size="small" color="success" />;
    }
    if (status === 'revoked') {
      return <Chip label="Revoked" size="small" color="error" />;
    }
    return <Chip label={status} size="small" />;
  };

  const getTierChip = (tier?: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning'> = {
      free: 'default',
      trial: 'default',
      launch: 'warning',
      rise: 'secondary',
      summit: 'success',
      expired: 'default',
    };
    return (
      <Chip
        label={tier || 'free'}
        size="small"
        color={colors[tier || 'free'] || 'default'}
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Platform Administration
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        Manage platform-wide settings and client organizations.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            icon={<Block />}
            iconPosition="start"
            label="Blocked Domains"
          />
          <Tab
            icon={<Business />}
            iconPosition="start"
            label="Organizations"
          />
          <Tab
            icon={<Category />}
            iconPosition="start"
            label="Service Types"
          />
          <Tab
            icon={<BugReport />}
            iconPosition="start"
            label="Developer Tools"
          />
        </Tabs>
      </Box>

      {/* Blocked Domains Tab */}
      <TabPanel value={tabValue} index={0}>
        <BlockedDomainsManager />
      </TabPanel>

      {/* Organizations Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Client Organizations</Typography>
              <Chip label={organizations.length} size="small" />
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              size="small"
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : filteredOrgs.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>
                {searchQuery ? 'No organizations match your search' : 'No organizations found'}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={40}></TableCell>
                      <TableCell>Organization</TableCell>
                      <TableCell>Slug</TableCell>
                      <TableCell>Tier</TableCell>
                      <TableCell>Contracts</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrgs.map((org) => (
                      <>
                        <TableRow
                          key={org._id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleToggleExpand(org._id)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {expandedOrg === org._id ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {org.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {org.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {org.slug}
                            </Typography>
                          </TableCell>
                          <TableCell>{getTierChip(org.subscription?.tier)}</TableCell>
                          <TableCell>
                            {getBoldsignStatusChip(org.boldsign?.status)}
                          </TableCell>
                          <TableCell>
                            {new Date(org.createdAt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 0 }}>
                            <Collapse in={expandedOrg === org._id} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                                <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                  <SubscriptionOverrideManager
                                    orgId={org._id}
                                    orgName={org.name}
                                    currentSubscription={org.subscription}
                                    onUpdate={fetchOrganizations}
                                  />
                                </Box>
                                <Box sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Contracts (BoldSign)
                                  </Typography>
                                  <ClientBoldSignManager
                                    organizationId={org._id}
                                    organizationName={org.name}
                                    organizationEmail={org.email}
                                  />
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Service Types Tab */}
      <TabPanel value={tabValue} index={2}>
        <ServiceTypesManager />
      </TabPanel>

      {/* Developer Tools Tab */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Sentry Error Tracking Test
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Use these buttons to verify Sentry is capturing errors correctly.
              Check your Sentry dashboard after clicking to confirm errors appear.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<BugReport />}
                onClick={() => {
                  try {
                    const testError = new Error('Test Frontend Error from Platform Admin');
                    captureException(testError, {
                      source: 'platform-admin-test',
                      timestamp: new Date().toISOString(),
                    });
                    setSnackbar({
                      open: true,
                      message: 'Frontend error sent to Sentry! Check your dashboard.',
                      severity: 'success',
                    });
                  } catch (err) {
                    setSnackbar({
                      open: true,
                      message: 'Failed to send error to Sentry',
                      severity: 'error',
                    });
                  }
                }}
              >
                Test Frontend Error
              </Button>

              <Button
                variant="contained"
                color="warning"
                startIcon={<BugReport />}
                onClick={async () => {
                  try {
                    await api.get('/admin/test-sentry');
                    setSnackbar({
                      open: true,
                      message: 'Backend error sent to Sentry! Check your dashboard.',
                      severity: 'success',
                    });
                  } catch (err) {
                    // This is expected - the endpoint throws an error
                    setSnackbar({
                      open: true,
                      message: 'Backend error sent to Sentry! Check your dashboard.',
                      severity: 'success',
                    });
                  }
                }}
              >
                Test Backend Error
              </Button>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Frontend Test:</strong> Captures an error directly via Sentry SDK (no crash).
              </Typography>
              <Typography variant="body2">
                <strong>Backend Test:</strong> Calls an API endpoint that throws an error (will show in network tab as 500).
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}
